<script setup>

import {ref, reactive, computed, watch, onMounted, onUpdated, onBeforeUnmount, nextTick} from 'vue'
import SlideoutWithSlots from "../elements/slideoutWithSlots.vue";
import LeadQuickview from "./leadQuickview.vue";

const leads = ref([]);
const totalLeads = ref(null);
const paginationLinks = ref([]);
const searchPossible = ref(true)
const fetchingLeadsStatus = ref('')
const loadingLeads = ref(false)
const selectedLeads = ref([])
const quickviewingLead = ref(null)
const quickviewSlideout = ref(null)
const saveFiltersState = ref('')

const searchParameters = reactive({
    fuzzy: null,
    myOwnContacts: true,
    tags: [],
    excludeTags: [],
    emailStatus: 'regardless-email',
    phoneStatus: 'regardless-phone',
    unsubscribedStatus: 'regardless-unsubscribed',
    orderBy: 'updated-lastest',
    perPage: 25,
    automationId: null,
    automationFilter: null,
});

const automationContext = reactive({
    id: null,
    filter: null,
    name: null,
});

const automationFilterStatusKeyMap = {
    opened: 'crm:automationFilter.status.opened',
    'not-opened': 'crm:automationFilter.status.notOpened',
    'not-delivered': 'crm:automationFilter.status.notDelivered',
};

const automationFilterActive = computed(() => Boolean(automationContext.id && automationContext.filter));
const automationFilterStatusLabel = computed(() => {
    const statusKey = automationFilterStatusKeyMap[automationContext.filter] ?? null;
    return statusKey ? _mft(statusKey) : '';
});
const automationFilterMessage = computed(() => {
    if (! automationFilterActive.value) {
        return '';
    }

    if (automationContext.name) {
        return _mft('crm:automationFilter.messageWithName', { name: automationContext.name });
    }

    return _mft('crm:automationFilter.messageWithoutName');
});

const perPageOptions = [
    {
        label: '25 ' + _mft('crm:perPage'),
        value: '25',
    },
    {
        label: '100 ' + _mft('crm:perPage'),
        value: '100',
    },
    {
        label: '250 ' + _mft('crm:perPage'),
        value: '250',
    },
]

const makeFetchLeadsRequest = async (link) => {
    let url = route('api.crm.filterLeads') + '?' + searchParametersUrlParams.value

    if (link !== null) {
        url = link.url + '&' + searchParametersUrlParams.value
    }
    try {
        fetchingLeadsStatus.value = 'waiting'
        loadingLeads.value = true
        const response = await axios.get(url);
        leads.value = response.data.leads.data;
        totalLeads.value = response.data.total_count;
        paginationLinks.value = response.data.leads.links;

        fetchingLeadsStatus.value = ''
        loadingLeads.value = false
        searchPossible.value = false;

    } catch (error) {
        fetchingLeadsStatus.value = 'error'
    }
}

const fetchLeads = (link = null) => {
    if (! searchPossible.value) {
        return
    }

    makeFetchLeadsRequest(link)
};

const onMultiDeleted = () => {
    selectedLeads.value = []
    makeFetchLeadsRequest(null);
}

const onTagsSelected = (selectedTags) => {
    searchParameters.tags = selectedTags.map(tag => tag.id)
    updateSearch()
}

const onExcludeTagsSelected = (selectedTags) => {
    searchParameters.excludeTags = selectedTags.map(tag => tag.id)
    updateSearch()
}

const updateSearch = () => {
    if (! searchPossible.value) {
        return
    }
    leads.value = [];
    totalLeads.value = null;
    paginationLinks.value = [];

    nextTick(() => {
        fetchLeads();
    })
};

const removeAutomationParamsFromUrl = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete('automationId');
    url.searchParams.delete('automationFilter');
    url.searchParams.delete('automationName');

    const newSearch = url.searchParams.toString();
    const newUrl = `${url.pathname}${newSearch ? `?${newSearch}` : ''}${url.hash}`;

    window.history.replaceState({}, '', newUrl);
};

const applyAutomationFilterFromUrl = () => {
    const params = new URLSearchParams(window.location.search);
    const filter = params.get('automationFilter');
    const id = params.get('automationId');
    const name = params.get('automationName');

    if (filter && id) {
        automationContext.id = id;
        automationContext.filter = filter;
        automationContext.name = name;
        searchParameters.automationId = id;
        searchParameters.automationFilter = filter;
        searchParameters.myOwnContacts = false;
    } else {
        automationContext.id = null;
        automationContext.filter = null;
        automationContext.name = null;
        searchParameters.automationId = null;
        searchParameters.automationFilter = null;
    }
};

const clearAutomationFilter = () => {
    automationContext.id = null;
    automationContext.filter = null;
    automationContext.name = null;
    searchParameters.automationId = null;
    searchParameters.automationFilter = null;
    removeAutomationParamsFromUrl();
    selectedLeads.value = [];
    searchPossible.value = true;
    updateSearch();
};

const saveFilters = () => {
    saveFiltersState.value = 'waiting'

    const persistentParameters = {
        fuzzy: searchParameters.fuzzy,
        myOwnContacts: searchParameters.myOwnContacts,
        tags: searchParameters.tags,
        excludeTags: searchParameters.excludeTags,
        emailStatus: searchParameters.emailStatus,
        phoneStatus: searchParameters.phoneStatus,
        unsubscribedStatus: searchParameters.unsubscribedStatus,
        orderBy: searchParameters.orderBy,
        perPage: searchParameters.perPage,
    }

    _mfPrefSet('crm-search-parameters', JSON.stringify(persistentParameters))
    setTimeout(function(){
        saveFiltersState.value = 'success'
    }, 150)
}

const clearSearch = () => {
    searchParameters.fuzzy = null;
    searchParameters.myOwnContacts = true;
    searchParameters.tags = [];
    searchParameters.excludeTags = [];
    searchParameters.emailStatus = 'regardless-email';
    searchParameters.phoneStatus = 'regardless-phone';
    searchParameters.unsubscribedStatus = 'regardless-unsubscribed';
    searchParameters.orderBy = 'updated-lastest';

    saveFilters();

    updateSearch();
};

const setPage = (link) => {
    searchPossible.value = true
    fetchLeads(link)
}

const onPerPageOptionChanged = () => {
    selectedLeads.value = []
    nextTick(() => {
        updateSearch()
    })
}

const selectLead = (lead) => {
    if (selectedLeads.value.includes(lead)) {
        selectedLeads.value = selectedLeads.value.filter((id) => id !== lead);
    } else {
        selectedLeads.value.push(lead);
    }
}

const selectAllLeads = () => {
    if (selectedLeads.value.length === leads.value.length) {
        selectedLeads.value = [];
    } else {
        selectedLeads.value = leads.value;
    }
}

const quickviewLead = (lead) => {
    quickviewingLead.value = lead;
}

const updateLeadFromQuickview = (lead) => {
    // This is a quick hack to avoid creating a new component and everything
    // We really only care about updating visible fields in the table anyway
    quickviewingLead.value.name = lead.name
    quickviewingLead.value.email = lead.email
    quickviewSlideout.value.close()
}

const searchParametersUrlParams = computed(() => {
    return Object.keys(searchParameters).map((key) => {
        return key + '=' + searchParameters[key]
    }).join('&')
})

const selectedLeadIds = computed(() => {
    return selectedLeads.value.map(lead => lead.id)
})

watch(searchParameters, (oldVal, newVal) => {
    searchPossible.value = true;
});

onMounted(() => {
    _mfPrefGet('crm-search-parameters').then(data => {
        if (data) {
            searchParameters.fuzzy = data.fuzzy ?? null
            searchParameters.myOwnContacts = data.myOwnContacts ?? true
            searchParameters.tags = data.tags ?? []
            searchParameters.excludeTags = data.excludeTags ?? []
            searchParameters.emailStatus = data.emailStatus ?? 'regardless-email'
            searchParameters.phoneStatus = data.phoneStatus ?? 'regardless-phone'
            searchParameters.unsubscribedStatus = data.unsubscribedStatus ?? 'regardless-unsubscribed'
            searchParameters.orderBy = data.orderBy ?? 'updated-lastest'
            searchParameters.perPage = data.perPage ?? 25
        }

        searchParameters.automationId = null
        searchParameters.automationFilter = null

        applyAutomationFilterFromUrl();
        fetchLeads();
    })
});
</script>

<template>

    <div>

        <div>
            <div class="flow-root mt-8">
                <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
                    <div class="flex items-center space-x-2 space-between">
                        <input-text
                            type="text"
                            :placeholder="_mft('crm:filters.search.placeholder')"
                            v-model:content="searchParameters.fuzzy"
                            @keydown.enter="updateSearch"
                            class="w-full !mb-1"
                        />
                        <button-base
                            :disabled="! searchPossible"
                            :style-type="searchPossible ? 'primary' : 'disabled'"
                            @click="updateSearch"
                            :state="fetchingLeadsStatus"
                        >
                            {{ _mft('crm:filters.search') }}
                        </button-base>
                    </div>

                    <div
                        v-if="automationFilterActive"
                        class="flex flex-col gap-3 p-4 mt-4 mb-4 text-sm bg-gray-50 border border-gray-200 rounded-card text-gray-800 md:flex-row md:items-center md:justify-between"
                    >
                        <div class="space-y-1">
                            <span class="block text-xs font-semibold uppercase tracking-wide text-gray-500">
                                {{ _mft('crm:automationFilter.bannerTitle') }}
                            </span>
                            <span class="block text-sm font-medium">{{ automationFilterMessage }}</span>
                        </div>
                        <div class="flex items-center gap-3">
                            <span
                                v-if="automationFilterStatusLabel"
                                class="inline-flex items-center px-2.5 py-1 text-xs font-semibold text-gray-700 bg-white rounded-full border border-gray-200"
                            >
                                {{ automationFilterStatusLabel }}
                            </span>
                            <button-base size="sm" style-type="secondary" @click="clearAutomationFilter">
                                {{ _mft('crm:automationFilter.clear') }}
                            </button-base>
                        </div>
                    </div>

                    <expandable-section>
                        <template v-slot:title>
                            {{ _mft('crm:filters.title') }}
                        </template>
                        <template v-slot:body>
                            <div class="grid grid-cols-1 gap-8 md:grid-cols-3">
                                <div>
                                    <label class="block mb-2 font-medium text-gray-700">
                                        {{ _mft('crm:filters.myOwnContacts.title') }}
                                    </label>
                                    <button-bar
                                        :options="[
                                            {
                                                label: _mft('crm:filters.myOwnContacts.myOwnContacts'),
                                                name: true,
                                            },
                                            {
                                                label: _mft('crm:filters.myOwnContacts.allContacts'),
                                                name: false,
                                            },
                                        ]"
                                        :selectedOptionName="searchParameters.myOwnContacts"
                                        @select="searchParameters.myOwnContacts = $event.name"
                                    />

                                    <label class="block mb-2 font-medium text-gray-700">
                                        {{ _mft('crm:filters.tags.title') }}
                                    </label>
                                    <tags-multi-select
                                        :allow-creation="false"
                                        :placeholder="_mft('crm:searchTags')"
                                        @tagsSelected="onTagsSelected"
                                    />
                                    <tags-multi-select
                                        :allow-creation="false"
                                        :placeholder="_mft('crm:excludeTags')"
                                        @tagsSelected="onExcludeTagsSelected"
                                    />

                                    <div class="flex space-x-2">
                                        <button-base
                                            size="sm"
                                            :state="saveFiltersState"
                                            @click="saveFilters"
                                            class="text-center"
                                        >
                                            {{ _mft('crm:filters.save') }}
                                        </button-base>

                                        <button-danger
                                            size="sm"
                                            @click="clearSearch"
                                            class="text-center"
                                        >
                                            {{ _mft('crm:filters.clear') }}
                                        </button-danger>
                                    </div>
                                </div>
                                <div>
                                    <label class="block mb-2 font-medium text-gray-700">
                                        {{ _mft('crm:filters.hasEmailAddress.title') }}
                                    </label>
                                    <button-bar
                                        :options="[
                                            {
                                                label: _mft('crm:filters.hasEmailAddress.withEmail'),
                                                name: 'with-email',
                                            },
                                            {
                                                label: _mft('crm:filters.hasEmailAddress.withoutEmail'),
                                                name: 'without-email',
                                            },
                                            {
                                                label: _mft('crm:filters.hasEmailAddress.both'),
                                                name: 'regardless-email',
                                            },
                                        ]"
                                        :selectedOptionName="searchParameters.emailStatus"
                                        @select="searchParameters.emailStatus = $event.name"
                                    />

                                    <label class="block mb-2 font-medium text-gray-700">
                                        {{ _mft('crm:filters.hasPhoneNumber.title') }}
                                    </label>
                                    <button-bar
                                        :options="[
                                            {
                                                label: _mft('crm:filters.hasPhoneNumber.withPhone'),
                                                name: 'with-phone',
                                            },
                                            {
                                                label: _mft('crm:filters.hasPhoneNumber.withoutPhone'),
                                                name: 'without-phone',
                                            },
                                            {
                                                label: _mft('crm:filters.hasPhoneNumber.both'),
                                                name: 'regardless-phone',
                                            },
                                        ]"
                                        :selectedOptionName="searchParameters.phoneStatus"
                                        @select="searchParameters.phoneStatus = $event.name"
                                    />

                                    <label class="block mb-2 font-medium text-gray-700">
                                        {{ _mft('crm:filters.unsubscribedStatus.title') }}
                                    </label>
                                    <button-bar
                                        :options="[
                                            {
                                                label: _mft('crm:filters.unsubscribedStatus.unsubscribed'),
                                                name: 'unsubscribed',
                                            },
                                            {
                                                label: _mft('crm:filters.unsubscribedStatus.subscribed'),
                                                name: 'subscribed',
                                            },
                                            {
                                                label: _mft('crm:filters.unsubscribedStatus.both'),
                                                name: 'regardless-unsubscribed',
                                            },
                                        ]"
                                        :selectedOptionName="searchParameters.unsubscribedStatus"
                                        @select="searchParameters.unsubscribedStatus = $event.name"
                                    />
                                </div>
                                <div>
                                    <label class="block mb-2 font-medium text-gray-700">
                                        {{ _mft('crm:filters.orderBy.title') }}
                                    </label>
                                    <button-bar
                                        :options="[
                                            {
                                                label: _mft('crm:filters.orderBy.nameAsc'),
                                                name: 'name-a',
                                            },
                                            {
                                                label: _mft('crm:filters.orderBy.nameDesc'),
                                                name: 'name-z',
                                            },
                                        ]"
                                        :selectedOptionName="searchParameters.orderBy"
                                        @select="searchParameters.orderBy = $event.name"
                                    />
                                    <button-bar
                                        :options="[
                                            {
                                                label: _mft('crm:filters.orderBy.createdDesc'),
                                                name: 'created-newest',
                                            },
                                            {
                                                label: _mft('crm:filters.orderBy.createdAsc'),
                                                name: 'created-oldest',
                                            },
                                        ]"
                                        :selectedOptionName="searchParameters.orderBy"
                                        @select="searchParameters.orderBy = $event.name"
                                    />
                                    <button-bar
                                        :options="[
                                            {
                                                label: _mft('crm:filters.orderBy.updatedDesc'),
                                                name: 'updated-lastest',
                                            },
                                            {
                                                label: _mft('crm:filters.orderBy.updatedAsc'),
                                                name: 'updated-oldest',
                                            },
                                        ]"
                                        :selectedOptionName="searchParameters.orderBy"
                                        @select="searchParameters.orderBy = $event.name"
                                    />
                                </div>
                            </div>
                        </template>
                    </expandable-section>

                    <div class="grid items-center h-12 grid-cols-3">
                        <div class="hidden md:block"></div>
                        <div
                            class="space-x-2 font-thin text-center text-gray-500"
                        >
                            <span v-if="loadingLeads" class="flex justify-center">
                                &nbsp;
                                <mf-spinner-medium class="w-8 h-4" />
                                &nbsp;
                            </span>

                            <div
                                v-else
                                class="text-xs lowercase transition-opacity"
                                :class="totalLeads !== null ? 'opacity-100' : 'opacity-0'"
                            >
                                <span>{{ totalLeads }} {{ _mft('crm:contacts') }}</span>
                            </div>
                        </div>
                        <div class="flex items-center justify-end col-span-2 md:col-span-1">
                            <input-select
                                v-if="selectedLeadIds.length === 0"
                                v-model:content="searchParameters.perPage"
                                :options="perPageOptions"
                                @update:content="onPerPageOptionChanged"
                                input-class="text-xs"
                                class="!mb-0"
                            ></input-select>
                        </div>
                    </div>

                    <table class="w-full text-left bg-white shadow-xl rounded-wl">
                        <thead class="sticky inset-0">
                            <tr class="border-b border-b-gray-200">
                                <th class="pl-1 pr-2 text-center bg-white md:pl-4 rounded-tl-wl min-w-1">
                                    <i
                                        class="text-xl text-gray-400 transition-all duration-75 cursor-pointer"
                                        :class="leads.length > 0 && selectedLeadIds.length === leads.length ? 'fa-square-check fa-regular text-myflowGreen-800' : 'fa-square fa-thin text-gray-500'"
                                        @click="selectAllLeads()"
                                    ></i>

                                </th>
                                <th scope="col" class="bg-white relative isolate py-3.5 pr-3 text-left text-sm font-semibold text-gray-900">
                                    {{ _mft('crm:leadIndex.columnLabel.name') }}
                                </th>
                                <th scope="col" class="bg-white hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 md:table-cell">
                                    {{ _mft('crm:leadIndex.columnLabel.company') }}
                                </th>
                                <th scope="col" class="bg-white relative py-3.5 pl-3">
                                    <span class="sr-only"></span>
                                </th>
                                <th scope="col" class="bg-white rounded-tr-wl z-20 relative py-3.5 pl-3">
                                    <span class="sr-only">
                                        {{ _mft('crm:leadIndex.columnLabel.tools') }}
                                    </span>
                                </th>
                            </tr>
                        </thead>
                        <tbody
                            v-if="leads != null"
                            class="divide-y"
                        >
                            <tr
                                v-for="lead in leads"
                                :key="lead.id"
                            >
                                <td class="pl-1 pr-2 text-center md:pl-4">
                                    <i
                                        class="text-xl text-gray-400 transition-all duration-75 cursor-pointer"
                                        :class="selectedLeadIds.includes(lead.id) ? 'fa-square-check fa-regular text-myflowGreen-800' : 'fa-square fa-thin text-gray-500'"
                                        @click="selectLead(lead)"
                                    ></i>
                                </td>
                                <td class="py-4 pr-3 text-sm font-medium text-gray-900">
                                    <a :href="route('crm.leads.view', lead.id)" target="_blank" class="text-black mf-underline">
                                        {{ lead.name }}
                                    </a><br>
                                    <small>{{ lead.email }}</small>
                                </td>
                                <td class="hidden px-3 py-4 text-sm text-gray-700 md:table-cell">
                                    {{ lead.title }}<br>
                                    <small>{{ lead.company }}</small>
                                </td>
                                <td class="relative py-4 pl-3 text-sm font-medium text-right">
                                    &nbsp;
                                </td>
                                <td class="relative py-4 pl-3 pr-4 text-sm font-medium text-right">
                                    <div class="flex flex-col items-end justify-end space-y-1 md:flex-row md:space-y-0 md:space-x-1 md:items-start">
                                        <button-secondary
                                            size="sm"
                                            @click="quickviewLead(lead)"
                                        >
                                            <i class="md:mr-1 fa-sharp fa-light fa-magnifying-glass-plus"></i>
                                            <span class="hidden md:inline">
                                                {{ _mft('crm:lead.quickview') }}
                                            </span>
                                        </button-secondary>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <nav class="flex items-center justify-between px-4 mt-8 sm:px-0">
            <div class="flex flex-1 w-0 -mt-px">
            </div>
            <div class="flex md:-mt-px">
                <a
                    v-for="(link, i) in paginationLinks"
                    :key="i"
                    :href="link.url"
                    class="relative w-8 h-8 transition-all border rounded-wl first:border-transparent last:border-transparent"
                    :class="[link.active ? 'items-center text-sm font-medium text-gray-600': 'hidden first:inline-flex last:inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700', link.active ? paginationLinks.length < 12 ? 'border-gray-300' : 'border-transparent' : paginationLinks.length >= 12 ? 'hidden' : 'border-transparent md:inline-flex']"
                    aria-current="page"
                    @click.prevent="setPage(link)"
                >
                    <span class="absolute inset-0 flex items-center justify-center">
                        <span class="" v-html="link.label"></span>
                    </span>
                </a>
            </div>
            <div class="flex justify-end flex-1 w-0 -mt-px">
            </div>
        </nav>

        <leads-multi-manage
            :leads="selectedLeads"
            @deleted="onMultiDeleted"
        />

        <slideout-with-slots
            v-if="quickviewingLead"
            ref="quickviewSlideout"
            @close="quickviewingLead = null"
        >
            <template #title>
                <h2>{{ quickviewingLead.name }}</h2>
            </template>
            <template #body>
                <div class="relative z-modal">
                    <lead-quickview
                        :lead="quickviewingLead"
                        @update:lead="updateLeadFromQuickview"
                    />
                </div>
            </template>
        </slideout-with-slots>


    </div>

</template>

<style>

</style>
